import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import useAnnotations from "admin/hooks/useAnnotations";
import { useConversationDiagnostics } from "admin/hooks/useConversationDiagnostics";
import useReviewStatus from "admin/hooks/useReviewStatus";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import { Activity, NotepadText } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent } from "~/components/ui/collapsible";
import useNextGenHeader, { type BreadcrumbItem } from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { cn } from "~/lib/utils";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { getAdminConversation, type AdminConversationView } from "~/services/admin-conversations";
import { AnnotationDrawer } from "./AnnotationDrawer";
import { ReadOnlyConversationView } from "./ReadOnlyConversationView";
import { SessionDiagnosticsBand, type SessionDiagnosticsBandProps } from "./SessionDiagnosticsBand";
import {
  TurnDiagnosticsProvider,
  type TurnDiagnosticsContextValue,
} from "./TurnDiagnosticsContext";
import { TurnDiagnosticsRail } from "./TurnDiagnosticsRail";
import { turnAnchorId } from "./useAnchoredScroll";
import { useTurns } from "./useTurns";

export const AdminConversationRoute = () => {
  const { conversationId } = useParams<{ conversationId: string }>();

  if (!conversationId) return null;

  return <AdminConversation key={conversationId} conversationId={conversationId} />;
};

interface AdminConversationProps {
  conversationId: string;
}

// Stable empty list so `useTurns` keeps its memo while the conversation loads.
const NO_MESSAGES: AdminConversationView["messages"] = [];

/** The unread-flag dot carries no text, so tests reach it by this handle. */
export const FLAGGED_DOT_TEST_ID = "diagnostics-flagged-dot";

/** Layout-only wrapper, so geometry tests reach it by this handle. */
const PANEL_COLUMN_TEST_ID = "conversation-panel-column";

const AdminConversation = ({ conversationId }: AdminConversationProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // The diagnostics layer is on by default: this view exists for triage.
  const [isDiagnosticsOn, setIsDiagnosticsOn] = useState(true);
  const [selectedTurnId, setSelectedTurnId] = useState<string | null>(null);
  // Set once this view writes the status. The conversation is fetched on mount
  // only, so its copy goes stale as soon as an operator changes it here.
  const [reviewStatusEdit, setReviewStatusEdit] = useState<ReviewStatus | undefined>(undefined);
  const [bannerView, setBannerView] = useState<AdminConversationView | null | undefined>(undefined);

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const view = await getAdminConversation(conversationId, { signal });
      setBannerView(view);
      return view;
    },
    [conversationId]
  );

  const reviewStatus = reviewStatusEdit ?? bannerView?.review_status;

  const { annotations, isLoading, createAnnotation, updateAnnotation, deleteAnnotation } =
    useAnnotations(conversationId);
  const { updateReviewStatus } = useReviewStatus(conversationId);

  const {
    byTurnId,
    sessionScore,
    severityCounts,
    flaggedTurnIds,
    isLoading: isLoadingDiagnostics,
    error: diagnosticsError,
  } = useConversationDiagnostics(conversationId);

  // Turn positions come from the same grouping the transcript renders, so
  // "Turn N of M" cannot drift from what an operator is looking at.
  const turns = useTurns(bannerView?.messages ?? NO_MESSAGES, false);
  const { ordinalByTurnId, totalTurns } = useMemo(() => {
    const ordinals = new Map<string, number>();
    let count = 0;
    for (const turn of turns) {
      if (turn.kind !== "assistant") continue;
      count += 1;
      const anchorId = turnAnchorId(turn.turnId);
      if (anchorId) ordinals.set(anchorId, count);
    }
    return { ordinalByTurnId: ordinals, totalTurns: count };
  }, [turns]);

  // Auto-open the sidebar once on first load if there are existing notes.
  // After that, defer to whatever the user has chosen.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (isLoading) return;
    hasAutoOpenedRef.current = true;
    if (annotations.length > 0) setIsDrawerOpen(true);
  }, [isLoading, annotations.length]);

  const handleReviewStatusChange = useCallback(
    async (next: ReviewStatus) => {
      await updateReviewStatus(next);
      setReviewStatusEdit(next);
    },
    [updateReviewStatus]
  );

  const hasDiagnostics = byTurnId.size > 0;
  // A revalidation that fails while rows are already cached leaves a perfectly
  // usable layer, so only a failure with nothing to fall back on is fatal.
  const isDiagnosticsFatal = diagnosticsError !== undefined && !hasDiagnostics;
  // Nothing distinguishes a turn the evaluator skipped from one it has not
  // reported on yet, so the per-turn markers wait for the query to settle and
  // then appear only alongside a band that can explain them.
  const isLayerReady = isDiagnosticsOn && hasDiagnostics && !isLoadingDiagnostics;

  const clearSelection = useCallback(() => setSelectedTurnId(null), []);

  const handleToggleDiagnostics = useCallback(() => {
    // Nothing is selectable while the layer is off, so the selection always
    // resets with the toggle.
    setIsDiagnosticsOn((on) => !on);
    setSelectedTurnId(null);
  }, []);

  const selectedDiagnostic = selectedTurnId ? byTurnId.get(selectedTurnId) : undefined;
  const selectedOrdinal = selectedTurnId ? ordinalByTurnId.get(selectedTurnId) : undefined;
  // The rail needs both the diagnostic and the turn's transcript position, so a
  // selection that resolves to neither shows nothing.
  const isRailOpen = selectedDiagnostic !== undefined && selectedOrdinal !== undefined;
  const isPanelOpen = isDrawerOpen || isRailOpen;

  const handleToggleNotes = useCallback(() => setIsDrawerOpen((open) => !open), []);

  const { prev: prevFlaggedId, next: nextFlaggedId } = adjacentFlagged(
    flaggedTurnIds,
    ordinalByTurnId,
    selectedOrdinal
  );

  useDocumentTitle(bannerView?.display_name ?? "");

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const crumbs: BreadcrumbItem[] = [{ label: "Admin" }];
    // Null for legacy conversations that predate agent binding — omit the
    // crumb rather than link to nothing.
    if (bannerView?.agent) {
      crumbs.push({
        label: bannerView.agent.display_name,
        href: `${ROUTE_NEXTGEN_VIEW}/agents/${bannerView.agent.id}/sessions`,
      });
    }
    crumbs.push({
      label:
        bannerView === undefined
          ? "Loading…"
          : (bannerView?.display_name ?? "Conversation not found"),
    });
    return crumbs;
  }, [bannerView]);

  const showFlaggedDot = !isDiagnosticsOn && flaggedTurnIds.length > 0;

  // The header's own `actions` slot, which puts these left of the account menu in
  // a `gap-2` row - the same place every other nextgen page hangs its controls,
  // and evenly spaced against the avatar for free. The band spans the window
  // below, so nothing here has an edge to stay flush with any more.
  const headerActions = useMemo<[ReactNode?, ReactNode?]>(
    () => [
      <Button
        key="toggle-diagnostics"
        variant="ghost"
        size="icon"
        className={cn(
          "relative size-8",
          isDiagnosticsOn ? "bg-primary/10 text-primary" : "text-muted-foreground"
        )}
        onClick={handleToggleDiagnostics}
        aria-label={isDiagnosticsOn ? "Hide turn diagnostics" : "Show turn diagnostics"}
        aria-pressed={isDiagnosticsOn}
      >
        <Activity className="size-4.5" />
        {showFlaggedDot && (
          <span
            aria-hidden="true"
            data-testid={FLAGGED_DOT_TEST_ID}
            className="absolute top-0.5 right-0.5 size-2 rounded-full bg-red-500 ring-2 ring-background"
          />
        )}
      </Button>,
      <Button
        key="toggle-notes"
        variant="ghost"
        size="icon"
        className={cn(
          "size-8",
          isDrawerOpen ? "bg-primary/10 text-primary" : "text-muted-foreground"
        )}
        onClick={handleToggleNotes}
        aria-label={isDrawerOpen ? "Collapse notes sidebar" : "Expand notes sidebar"}
        aria-pressed={isDrawerOpen}
      >
        <NotepadText className="size-4.5" />
      </Button>,
    ],
    [isDiagnosticsOn, isDrawerOpen, showFlaggedDot, handleToggleDiagnostics, handleToggleNotes]
  );

  useNextGenHeader({ breadcrumbs, actions: headerActions });

  // One column carries both panels, stacked, so an operator can read a turn's
  // diagnosis and take notes on it at the same time. The column owns the single
  // left edge, the width transition and the seam between the panels; the panels
  // split its height evenly and each scroll on their own.
  // N.B. the seam is the column's rather than the rail's because the drawer stays
  // mounted while collapsed: a border the rail carried itself would survive as a
  // hairline pinned to the bottom edge whenever the rail is showing alone.
  const rightPanel = (
    <div
      data-testid={PANEL_COLUMN_TEST_ID}
      className={cn(
        "flex shrink-0 flex-col overflow-hidden transition-all duration-300 ease-in-out",
        isPanelOpen ? "w-[364px] border-l border-border" : "w-0",
        isRailOpen && isDrawerOpen && "divide-y divide-border"
      )}
    >
      {isRailOpen && (
        <TurnDiagnosticsRail
          diagnostic={selectedDiagnostic}
          ordinal={selectedOrdinal}
          totalTurns={totalTurns}
          onClose={clearSelection}
          onPrevFlagged={() => {
            if (prevFlaggedId) setSelectedTurnId(prevFlaggedId);
          }}
          onNextFlagged={() => {
            if (nextFlaggedId) setSelectedTurnId(nextFlaggedId);
          }}
          hasPrevFlagged={prevFlaggedId !== null}
          hasNextFlagged={nextFlaggedId !== null}
        />
      )}
      <AnnotationDrawer
        isOpen={isDrawerOpen}
        annotations={annotations}
        isLoading={isLoading}
        onClose={handleToggleNotes}
        createAnnotation={createAnnotation}
        updateAnnotation={updateAnnotation}
        deleteAnnotation={deleteAnnotation}
      />
    </div>
  );

  // N.B. the Collapsible is mounted whether or not the layer is on, because the
  // band has to stay in the tree long enough to animate out. While closed Radix
  // unmounts the content and leaves the wrapper `hidden`, so nothing renders,
  // nothing is focusable and nothing reaches the a11y tree - the same end state
  // as an absent band, reached after the collapse instead of before it.
  const topBand = (
    <Collapsible open={isDiagnosticsOn} className="shrink-0">
      <CollapsibleContent animated>
        <DiagnosticsTopBand
          isLoading={isLoadingDiagnostics}
          hasFatalError={isDiagnosticsFatal}
          hasDiagnostics={hasDiagnostics}
          sessionScore={sessionScore}
          severityCounts={severityCounts}
          // A diagnostic can name a turn this transcript does not render, so the
          // difference is not guaranteed to be positive.
          unscoredCount={Math.max(0, totalTurns - byTurnId.size)}
          reviewStatus={reviewStatus}
          onReviewStatusChange={handleReviewStatusChange}
        />
      </CollapsibleContent>
    </Collapsible>
  );

  const diagnosticsValue = useMemo<TurnDiagnosticsContextValue | null>(
    () =>
      isLayerReady
        ? { byTurnId, ordinalByTurnId, totalTurns, selectedTurnId, selectTurn: setSelectedTurnId }
        : null,
    [isLayerReady, byTurnId, ordinalByTurnId, totalTurns, selectedTurnId]
  );

  // N.B. the provider is mounted unconditionally and carries `null` when the
  // layer is off. Wrapping conditionally would change the element type here and
  // remount the conversation on every toggle, refetching it and blanking the
  // transcript. A `null` value already makes every per-turn marker and the
  // selected-turn tint render nothing.
  return (
    <TurnDiagnosticsProvider value={diagnosticsValue}>
      <ReadOnlyConversationView
        conversationId={conversationId}
        isAdmin
        fetcher={fetcher}
        bannerSubtitle="Admin read-only view."
        ownerVerb="Owned by"
        notFoundTitle="Conversation not found"
        notFoundBody="It may have been deleted, or the ID is wrong."
        rightPanel={rightPanel}
        topBand={topBand}
        readOnlyFeedback
      />
    </TurnDiagnosticsProvider>
  );
};

interface DiagnosticsTopBandProps extends SessionDiagnosticsBandProps {
  isLoading: boolean;
  hasFatalError: boolean;
  hasDiagnostics: boolean;
}

/** Whichever strip the diagnostics layer can honestly show for this session. */
const DiagnosticsTopBand = ({
  isLoading,
  hasFatalError,
  hasDiagnostics,
  ...band
}: DiagnosticsTopBandProps) => {
  if (hasFatalError) {
    return (
      <BandNotice role="alert" className="text-destructive">
        Failed to load diagnostics. Please try again.
      </BandNotice>
    );
  }
  if (isLoading) return null;
  if (!hasDiagnostics) {
    return <BandNotice>Diagnostics have not run for this session yet.</BandNotice>;
  }
  return <SessionDiagnosticsBand {...band} />;
};

interface BandNoticeProps {
  children: string;
  role?: "alert";
  className?: string;
}

/** The band's slot when there is no rollup to show, keeping the layout identical. */
const BandNotice = ({ children, role, className }: BandNoticeProps) => (
  <div
    role={role}
    className={cn(
      "shrink-0 border-b border-border bg-muted/40 px-6 py-3 text-xs text-muted-foreground",
      className
    )}
  >
    {children}
  </div>
);

/**
 * The flagged turns immediately before and after a transcript position. Walking
 * positions rather than the flagged list keeps the footer usable when the open
 * turn is not itself flagged, which is any turn opened from its own icon.
 */
function adjacentFlagged(
  flaggedTurnIds: readonly string[],
  ordinalByTurnId: ReadonlyMap<string, number>,
  fromOrdinal: number | undefined
): { prev: string | null; next: string | null } {
  let prev: string | null = null;
  let next: string | null = null;
  if (fromOrdinal === undefined) return { prev, next };

  let prevOrdinal = -Infinity;
  let nextOrdinal = Infinity;
  for (const turnId of flaggedTurnIds) {
    const ordinal = ordinalByTurnId.get(turnId);
    if (ordinal === undefined) continue;
    if (ordinal < fromOrdinal && ordinal > prevOrdinal) {
      prev = turnId;
      prevOrdinal = ordinal;
    }
    if (ordinal > fromOrdinal && ordinal < nextOrdinal) {
      next = turnId;
      nextOrdinal = ordinal;
    }
  }
  return { prev, next };
}
